import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Modal, Select, Show } from "shared/components";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Label, LabelSize } from "shared/components/forms/Label";
import {
  lockboxesApi,
  Lockbox,
  GeneratedLockboxCode,
  LockboxCodeType,
  RentlyDeviceType,
  SENTRILOCK_CODE_DURATIONS,
  SentrilockCodeDuration
} from "api/landlord/lockboxes";
import { getApiErrorKey, getErrorMessage } from "utils/error";
import { LOCKBOX_CODE_TYPE_LABELS } from "./constants";

const ERROR_MESSAGES: Record<string, string> = {
  lockbox_account_unassigned:
    "Assign a vendor account to this lockbox before generating a code. You can do that from the Lockboxes screen.",
  integration_not_connected:
    "This lockbox's vendor isn't connected. Add credentials under Settings → Integrations first.",
  invalid_duration: "Pick one of the offered durations and try again.",
  duplicate_request:
    "A code is already being generated for this lockbox. Give it a moment and try again.",
  sentrilock_no_code_returned:
    "SentriLock didn't return a code for this serial number. Check the serial number and the assigned account, then try again.",
  sentrilock_no_shackle_code_returned:
    "SentriLock didn't return a shackle code for this lockbox. Check the serial number and the assigned account, then try again.",
  shackle_code_unsupported:
    "Shackle codes are only available for SentriLock lockboxes.",
  codebox_no_code_returned:
    "Codebox didn't return a code for this serial number. Check the serial number and the assigned account, then try again.",
  rently_no_code_returned:
    "Rently didn't return a code for this serial number. Check the serial number and the assigned account, then try again.",
  rently_device_type_missing:
    "Set this lockbox's device type before generating a code. You can do that from the Lockboxes screen."
};

// Only a Blue Lock honours the duration we ask for; Rently times the other two itself, so the
// picker is hidden for them and we say up front what they will actually get.
const RENTLY_DISCLOSURES: Record<RentlyDeviceType, string | null> = {
  blue_lock: null,
  orange_lockbox:
    "Rently Orange Lockbox codes work for the rest of today and can't be shortened or canceled once created.",
  smart_bolt:
    "Rently Smart Bolt codes can only be used once, expire 3 hours after you generate them, and can't be canceled."
};

const generateCodeErrorMessage = (error: unknown) => {
  const key = getApiErrorKey(error);
  return key && ERROR_MESSAGES[key]
    ? ERROR_MESSAGES[key]
    : getErrorMessage(error);
};

const DURATION_LABELS: Record<SentrilockCodeDuration, string> = {
  30: "30 minutes",
  60: "1 hour",
  120: "2 hours",
  240: "4 hours"
};

const DURATION_OPTIONS = SENTRILOCK_CODE_DURATIONS.map((value) => ({
  label: DURATION_LABELS[value],
  value
}));

const DEFAULT_CODE_TYPE: LockboxCodeType = "access";

const CODE_TYPE_TABS: Tab<LockboxCodeType>[] = [
  { value: "access", label: LOCKBOX_CODE_TYPE_LABELS.access },
  { value: "shackle", label: LOCKBOX_CODE_TYPE_LABELS.shackle }
];

const CODE_TYPE_DESCRIPTIONS: Record<LockboxCodeType, string> = {
  access: "Opens the key compartment.",
  shackle:
    "Releases the shackle so you can put the lockbox on a door or take it off."
};

const DURATION_NOTE = "Starts as soon as you generate it";

const SENTRILOCK_NOTES: Record<LockboxCodeType, string> = {
  access: DURATION_NOTE,
  shackle:
    "Starts as soon as you generate it. Anyone with this code can take the lockbox off the door. Share it only with whoever is installing or removing it."
};

export const GenerateLockboxCodeModal = ({
  opened,
  onClose,
  onGenerated,
  lockbox
}: {
  opened: boolean;
  onClose: () => void;
  onGenerated: (result: GeneratedLockboxCode) => void;
  lockbox: Lockbox;
}) => {
  const queryClient = useQueryClient();
  const isSentrilock = lockbox.vendor === "sentrilock";
  const isIgloohome = lockbox.vendor === "igloohome";
  const isRently = lockbox.vendor === "rently";
  const isCodebox = !isSentrilock && !isIgloohome && !isRently;
  const needsDuration =
    isSentrilock || (isRently && lockbox.device_type === "blue_lock");
  const rentlyDisclosure =
    isRently && lockbox.device_type
      ? RENTLY_DISCLOSURES[lockbox.device_type]
      : null;

  const [duration, setDuration] = useState<SentrilockCodeDuration>(
    SENTRILOCK_CODE_DURATIONS[0]
  );
  const [codeType, setCodeType] = useState<LockboxCodeType>(DEFAULT_CODE_TYPE);
  const effectiveCodeType = isSentrilock ? codeType : DEFAULT_CODE_TYPE;

  const resetForm = () => {
    setDuration(SENTRILOCK_CODE_DURATIONS[0]);
    setCodeType(DEFAULT_CODE_TYPE);
  };

  const { mutate: generate, isPending } = useMutation({
    mutationKey: ["LOCKBOX", "GENERATE_CODE", "CREATE", lockbox.id],
    mutationFn: () =>
      lockboxesApi.generateCode(lockbox.id, {
        ...(needsDuration ? { duration_minutes: duration } : {}),
        ...(isSentrilock ? { code_type: effectiveCodeType } : {})
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
      onGenerated(result);
      resetForm();
    },
    onError: (err) => {
      toast.error(generateCodeErrorMessage(err));
    }
  });

  const handleClose = () => {
    if (isPending) return;
    resetForm();
    onClose();
  };

  return (
    <Modal
      zIndex={120}
      opened={opened}
      onClose={handleClose}
      panelClassName="relative max-w-[366px] w-full border border-alpha-100 p-6 space-y-4 shadow-lg"
    >
      <div className="space-y-2">
        <p className="text-center text-xl font-bold text-content-1000">
          Generate lockbox code
        </p>
        <Show if={needsDuration && !isSentrilock}>
          <p className="text-center text-base text-content-500">
            How long should the code work?
          </p>
        </Show>
      </div>

      <Show if={isSentrilock}>
        <div>
          <Label size={LabelSize.SM} as="span" className="block">
            <span className="font-medium text-content-1000">
              What do you need?
            </span>
          </Label>
          <Tabs
            size="sm"
            tabStyle="outline"
            fullWidthTabs
            // Locked while a code is being generated, so the pick can't change under the request.
            tabs={CODE_TYPE_TABS.map((tab) => ({
              ...tab,
              disabled: isPending
            }))}
            active={codeType}
            onChange={setCodeType}
          />
          <p className="mt-2 text-sm text-content-400">
            {CODE_TYPE_DESCRIPTIONS[codeType]}
          </p>
        </div>
      </Show>

      <div className="space-y-4">
        <Show if={needsDuration}>
          <div className="space-y-4">
            <div>
              <Show if={isSentrilock}>
                <Label
                  size={LabelSize.SM}
                  htmlFor="code-duration"
                  className="block"
                >
                  <span className="font-medium text-content-1000">
                    How long should the code work?
                  </span>
                </Label>
              </Show>
              <Select
                id="code-duration"
                aria-label="Code duration"
                options={DURATION_OPTIONS}
                value={duration}
                searchable={false}
                clearable={false}
                onChange={(e) => {
                  const parsed = Number(e.target.value);
                  const isValidDuration = (
                    SENTRILOCK_CODE_DURATIONS as readonly number[]
                  ).includes(parsed);
                  if (isValidDuration)
                    setDuration(parsed as SentrilockCodeDuration);
                }}
              />
            </div>
            <div className="rounded-lg bg-warning-50 p-4">
              <p className="text-sm font-medium text-warning-700">
                {isSentrilock
                  ? SENTRILOCK_NOTES[effectiveCodeType]
                  : DURATION_NOTE}
              </p>
            </div>
          </div>
        </Show>

        <Show if={isCodebox}>
          <div className="rounded-lg bg-warning-50 p-4">
            <p className="text-sm font-medium text-warning-700">
              Codebox codes work for the rest of today and can&apos;t be
              shortened or canceled once created.
            </p>
          </div>
        </Show>

        <Show if={Boolean(rentlyDisclosure)}>
          <div className="rounded-lg bg-warning-50 p-4">
            <p className="text-sm font-medium text-warning-700">
              {rentlyDisclosure}
            </p>
          </div>
        </Show>

        <Show if={isIgloohome}>
          <div className="rounded-lg bg-warning-50 p-4">
            <p className="text-sm font-medium text-warning-700">
              Igloohome codes work for 24 hours and can only be used
              once&mdash;they can&apos;t be shortened or canceled once created.
            </p>
          </div>
        </Show>

        {/* One "Generate" for both code types: the switch and the note already say which kind, and a
            longer label doesn't fit half of the 366px pop-up. */}
        <div className="flex w-full gap-2">
          <Button className="w-full" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            onClick={() => generate()}
            loading={isPending}
            disabled={isPending}
          >
            Generate
          </Button>
        </div>
      </div>
    </Modal>
  );
};
